import { Badge, Button, Card, Flex, theme } from 'antd'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface SectionRow {
  content: ReactNode
  label: string
}

/*
 * Component
 */

interface Props {
  /**
   * Whether the current values are a legal thing to write. Defaults to true;
   * a section sets it to false while a field holds a value the mutation would
   * reject.
   */
  canSave?: boolean
  /**
   * Whether the section's values differ from the server's. Omit for a section
   * that holds nothing editable — it then renders no footer and no status.
   */
  isDirty?: boolean
  isLoading?: boolean
  onDiscard?: () => void
  onSave?: () => void
  rows: SectionRow[]
  title: string
}

/**
 * Groups one block of the customer details modal under its own heading. A
 * section that can be saved carries its own Save / Discard pair, so an edit
 * here can never commit a field belonging to another section.
 *
 * Rows are label/value pairs separated by a hairline, rather than a bordered
 * table nested in a bordered card: two borders and two radii meeting at the
 * first and last row is what notched the corners.
 */
const SectionCard = ({
  canSave = true,
  isDirty,
  isLoading,
  onDiscard,
  onSave,
  rows,
  title,
}: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const isSavable = isDirty !== undefined

  return (
    <Card
      size="small"
      title={title}
      extra={
        isSavable && (
          <Badge
            status={isDirty ? 'processing' : 'default'}
            text={
              isDirty
                ? t('customerDetails.status.unsaved')
                : t('customerDetails.status.saved')
            }
          />
        )
      }
      styles={{ body: { padding: 0 } }}
    >
      {rows.map((row, index) => (
        <div
          key={row.label}
          style={{
            alignItems: 'center',
            borderTop:
              index === 0
                ? undefined
                : `1px solid ${token.colorBorderSecondary}`,
            display: 'grid',
            gap: token.marginSM,
            gridTemplateColumns: '180px minmax(0, 1fr)',
            minHeight: 34,
            padding: `${token.paddingXXS}px ${token.paddingSM}px`,
          }}
        >
          <span style={{ color: token.colorTextDescription }}>{row.label}</span>
          <div style={{ minWidth: 0 }}>{row.content}</div>
        </div>
      ))}
      {isSavable && (
        <Flex
          gap={token.marginXS}
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            padding: token.paddingSM,
          }}
        >
          <Button
            block
            type="default"
            disabled={!isDirty || isLoading}
            onClick={onDiscard}
          >
            {t('labels.discard')}
          </Button>
          <Button
            block
            type="primary"
            disabled={!isDirty || !canSave}
            loading={isLoading}
            onClick={onSave}
          >
            {t('labels.save')}
          </Button>
        </Flex>
      )}
    </Card>
  )
}

export default SectionCard
